import { Link, useLoaderData } from '@remix-run/react';
import { DataFunctionArgs, MetaFunction } from '@remix-run/server-runtime';
import { useRef, useState } from 'react';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { FiltersButton } from '~/components/FiltersButton';
import FacetFilterControls from '~/components/facet-filter/FacetFilterControls';
import { FacetFilterTracker } from '~/components/facet-filter/facet-filter-tracker';
import { AppCard } from '~/components/products/AppCard';
import { APP_META_TITLE } from '~/constants';
import { getCategories, searchRecords } from '~/generated/bp-api';
import { FacetValueResult } from '~/generated/graphql';
import { usePreviews } from '~/generated/tds/bp-utils';

export const meta: MetaFunction = ({ data }) => {
  return {
    title: data?.collection
      ? `${data.collection?.name} - ${APP_META_TITLE}`
      : APP_META_TITLE,
  };
};


const getFacetArguments = (requestUrl: URL) => {
  const term = requestUrl.searchParams.get('q');
  const selectedFacetsRaw = requestUrl.searchParams.getAll('fvid');
  // TODO: validate facets first
  let selectedFacets = selectedFacetsRaw.map(raw => {
    const values = decodeURIComponent(raw).split(':');
    return {
      spec: values[0],
      value: values[1]
    }
  });
  const keyMap = selectedFacets.reduce((r, { spec, value }) => {
    r[spec] = r[spec] || [];
    r[spec].push(value);
    return r;
  }, Object.create(null));
  const result = Object.entries(keyMap).reduce((acc: { property: string, values: string[] }[], [key, value]) => {
    const mapFn = (key: string, value: string) => ({ property: key === 'category' ? key : `specs.${key}`, values: [...value] })
    acc.push(mapFn(key, value as string));
    return acc;
  }, []);

  return {
    term,
    selectedFacets: result
  }
}

export async function loader({ params, request, context }: DataFunctionArgs) {
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const pageSize = searchParams.get("pageSize") ? parseInt(searchParams.get("pageSize")!) : 20;
  const pageIndex = searchParams.get("pageIndex") ? parseInt(searchParams.get("pageIndex")!) : 0;
  const size = (pageIndex + 1) * pageSize;

  const facetArguments = getFacetArguments(url);

  const categories = await getCategories();

  let noFilterDimensions;
  const content = await searchRecords('solution',
                                      '',
                                      [
                                        { propertyName: 'specs.resources', aggType: 'terms' },
                                        { propertyName: 'specs.products', aggType: 'terms' },
                                        { propertyName: 'specs.training', aggType: 'terms' }
                                      ],
                                      facetArguments.selectedFacets,
                                      size,
                                      0);
  const hasMore = content?.records && content?.pager ? content.records.length < content.pager.totalRecords : false;
  if (!facetArguments.selectedFacets?.length) {
    noFilterDimensions = content?.dimensions;
  } else {
    const allDimensionsResponse = await searchRecords('solution',
                                                      '',
                                                      [
                                                        { propertyName: 'specs.resources', aggType: 'terms' },
                                                        { propertyName: 'specs.products', aggType: 'terms' },
                                                        { propertyName: 'specs.training', aggType: 'terms' }
                                                      ],
                                                      [],
                                                      0);
    noFilterDimensions = allDimensionsResponse?.dimensions;
  }

  const dimensions = content?.dimensions.map((d: any) => {
    const facet = facetArguments.selectedFacets.find(s => s.property.includes(d.name));
    return { ...d, values: d.values.map((v :any) => { return { ...v, selected: !!facet?.values.find(facetValue => v.value === facetValue) } }) }
  });

  const flatten = (arr: any) => arr.flatMap(({ values, ...rest }: { values: any[] }) =>
    values.map((o: any) => ({
      ...rest,
      ...o
    }))
  );

  function toTitleCase(str: string) {
    if (!str) {
      return '';
    }
    return str.replace(/\w\S*/g, function (txt: string) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
  const facetValueIds: any[] = [];
 
  const transformDimension = (searchResults: any[]):  FacetValueResult[] => {
    return searchResults.map((r: any) => {
      if (r.selected) {
        facetValueIds.push(r.value);
      }
      
      const label = categories?.find(c => c.id == r.value)?.name ?? toTitleCase(r.value);
      const facetLabel = toTitleCase(r.name)
  
      return {
        count: r.Count,
        facetValue: {
          id: r.value,
          name: label,
          selected: r.selected,
          facet: {
            id: r.name,
            name: facetLabel
          }
        }
      } as any;
    })
  }


  const facetValues: FacetValueResult[] = transformDimension(flatten(dimensions));
  const unfilteredFacetValuesRaw: FacetValueResult[] = transformDimension(flatten(noFilterDimensions));
  const total = unfilteredFacetValuesRaw.reduce((f, cur) => f + cur.count, 0)

  const unfilteredFacetValues = { facetValues: unfilteredFacetValuesRaw, totalItems: total };



  if (content.records?.length) {
    content.records = usePreviews(content.records, '320x180');
  }

  return {
    collection: { name: 'All Solutions', breadcrumbs: [] },
    pager: content?.pager,
    dimensions,
    facetValues,
    facetValueIds,
    unfilteredFacetValues,
    content,
    pageSize,
    pageIndex,
    hasMore
  };
}

export default function CollectionSlug() {
  const { content, collection, facetValues, facetValueIds,  unfilteredFacetValues, pageSize, pageIndex, hasMore } =
    useLoaderData<typeof loader>();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const facetValuesTracker = useRef(new FacetFilterTracker());

  // This is the true total.  Need to look into issue with facetValuesTracker
  // and sort out what resultWithoutFacetValueFilters should be

  facetValuesTracker.current.update(
    facetValues,
    unfilteredFacetValues,
    facetValueIds,
  );
  return (
    <div className="max-w-6xl mx-auto px-4">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl sm:text-5xl font-light tracking-tight text-gray-900 my-8">
          {collection.name}
        </h2>

        <FiltersButton
          filterCount={facetValueIds.length}
          onClick={() => setMobileFiltersOpen(true)}
        />
      </div>

      <Breadcrumbs items={collection.breadcrumbs}></Breadcrumbs>
      {/* {content?.data?.records.map((c: any) => (
        <div>{ c.slug }</div>
      ))} */}



      <div className="mt-6 grid sm:grid-cols-5 gap-x-4">
        <FacetFilterControls
          facetFilterTracker={facetValuesTracker.current}
          mobileFiltersOpen={mobileFiltersOpen}
          setMobileFiltersOpen={setMobileFiltersOpen}
        />
        <div className="sm:col-span-5 lg:col-span-4">
          <div className="grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-4 xl:gap-x-8">
            {content?.records.map(({ id, attributes }: { id: string, attributes: any }) => (
              <Link key={id} className="component-card flex flex-col" to={ `/apps/${attributes.slug}`}>  
                <AppCard app={attributes}></AppCard>
              </Link>

            ))}
          </div>
        </div>
      </div>
      { hasMore ? (
        <div className="mt-6 lg:mt-10 flex justify-center">
          <Link preventScrollReset to={`/collections/all?pageSize=${pageSize || 10}&pageIndex=${pageIndex ? pageIndex + 1: 1}`} className="bg-primary border border-transparent py-2 px-6 flex items-center justify-center text-base font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-sky-500">
              Load More            
          </Link>
        </div>
      ) : ''}
      {/* Filter controls: {JSON.stringify(dimensions)} */}

    </div>
  );
}

export function CatchBoundary() {
  return (
    <div className="max-w-6xl mx-auto px-4">
      <h2 className="text-3xl sm:text-5xl font-light tracking-tight text-gray-900 my-8">
        Collection not found!
      </h2>
      <div className="mt-6 grid sm:grid-cols-5 gap-x-4">
        <div className="space-y-6">
          <div className="h-2 bg-slate-200 rounded col-span-1"></div>
          <div className="h-2 bg-slate-200 rounded col-span-1"></div>
          <div className="h-2 bg-slate-200 rounded col-span-1"></div>
        </div>
        <div className="sm:col-span-5 lg:col-span-4">
          <div className="grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-4 xl:gap-x-8">
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
